import { Button, Form, Select, Space } from "antd";
import { useEffect } from "react";
import { useAppSelector } from "../../../app/store";
import FilterIcon from "../../../assets/icons/FilterIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import { useGetDepartmentsQuery } from "../../../features/department/departmentApi";
import { useGetDesignationsQuery } from "../../../features/designation/designationApi";
import { useGetEmployeesQuery } from "../../../features/employee/employeeApi";
import { useGetWorkStationsQuery } from "../../../features/workStation/workStationApi";
import useError from "../../../hooks/useError";
import { CustomAttendanceFilterType } from "../../../pages/admin/Attendance/CustomAttendance";
import { attendanceTypeList } from "../../../pages/admin/Employees/Employees";
import { ModalComponentProps } from "../../../types";
import {
  selectArrayOption,
  selectArrayOptionWithAll,
  selectSearchOption,
  selectStringOption,
} from "../../../utils/utils";
import ModalCustomTitle from "../../common/ModalCustomTitle";
import CustomModal from "../../ui/CustomModal";
import ListWithCheckbox from "./_components/ListWithCheckbox";

interface FilterModalProps extends ModalComponentProps {
  filter?: Omit<CustomAttendanceFilterType, "search_type">;
  setFilter: (values: CustomAttendanceFilterType) => void;
}

export default function FilterModal({
  handleClose,
  filter,
  setFilter,
  ...rest
}: FilterModalProps) {
  const [form] = Form.useForm();
  const business_department_id = Form.useWatch("business_department_id", form);
  const { authInfo } = useAppSelector((state) => state.auth);
  const {
    isLoading: isDepartmentLoading,
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const {
    isLoading: isWorkStationLoading,
    isError: isWorkStationError,
    error: workStationError,
    data: workStationData,
  } = useGetWorkStationsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const {
    data: designationData,
    isError: isDesignationError,
    error: designationError,
  } = useGetDesignationsQuery(
    {
      params: {
        limit: 0,
        skip: 0,
        ...(business_department_id
          ? { business_department_id: business_department_id?.toString() || "" }
          : {}),
      },
    },
    {
      skip: !business_department_id,
    }
  );
  const { isError, error, data } = useGetEmployeesQuery({
    params: {
      status: "active",
      manager_id: authInfo?.role === "Manager" ? authInfo?.id : undefined,
      skip: 0,
      limit: 0,
    },
  });
  useError(isDepartmentError, departmentError, "Get department data fail");
  useError(isDesignationError, designationError, "Get designation fail");
  useError(isWorkStationError, workStationError, "Work station get fail");
  useError(isError, error, "Employee get fail");

  const onFinish = (values: CustomAttendanceFilterType) => {
    setFilter({ ...filter, ...values });
    handleClose();
  };

  useEffect(() => {
    form.setFieldsValue(filter);
  }, [filter, form]);

  const handleResetFilter = () => {
    const reset: CustomAttendanceFilterType = {
      business_department_id: undefined,
      business_role_id: undefined,
      business_work_station_id: undefined,
      status: "active",
      attendanceType: undefined,
      business_member_ids: [],
    };

    setFilter(reset);
    form.setFieldsValue(reset);
    handleClose();
  };

  return (
    <CustomModal
      footer={false}
      handleClose={handleClose}
      width={553}
      closeIcon={null}
      {...rest}
    >
      <ModalCustomTitle
        title="Filter"
        icon={<FilterIcon width={24} height={24} />}
        iconClassName="!bg-[#f9fafb] disabled:!text-body-text"
        handleClose={handleClose}
      />

      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="business_department_id" className="mb-4">
            <Select
              placeholder="Department"
              size="middle"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={selectArrayOption(
                departmentData?.data?.departments || [],
                "name",
                "id"
              )}
              suffixIcon={<SelectDownIcon />}
              loading={isDepartmentLoading}
              onChange={() =>
                form.setFieldsValue({ business_role_id: undefined })
              }
            />
          </Form.Item>
          <Form.Item name="business_role_id" className="mb-4">
            <Select
              placeholder="Designation"
              size="middle"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={selectArrayOption(
                designationData?.data?.roles || [],
                "name",
                "id"
              )}
              suffixIcon={<SelectDownIcon />}
              disabled={!business_department_id}
            />
          </Form.Item>
          <Form.Item name="business_work_station_id" className="mb-4">
            <Select
              placeholder="Work Station"
              size="middle"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={selectArrayOptionWithAll(
                workStationData?.data?.workStations || [],
                "name",
                "id"
              )}
              suffixIcon={<SelectDownIcon />}
              loading={isWorkStationLoading}
            />
          </Form.Item>

          <Form.Item name="attendanceType" className="mb-4">
            <Select
              placeholder="Attendance Type"
              size="middle"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={attendanceTypeList}
              suffixIcon={<SelectDownIcon />}
            />
          </Form.Item>

          <Form.Item name="status" className="mb-4">
            <Select
              allowClear
              showSearch
              placeholder="Status"
              filterOption={selectSearchOption}
              options={selectStringOption(
                ["all", "active", "inactive", "invited"],
                "string",
                true
              )}
              // className="min-w-[90px]"
              suffixIcon={<SelectDownIcon />}
              size="middle"
            />
          </Form.Item>

          <Form.Item name="business_member_ids" className="mb-4">
            <ListWithCheckbox
              list={
                data?.data?.employees?.map((el) => ({
                  label: el?.member?.profile?.name || "n/a",
                  opLabel: el?.employee_id || "n/a",
                  value: el?.id,
                })) || []
              }
            />
          </Form.Item>
        </div>

        <Space className="w-full mt-2" wrap size={12}>
          <Button
            className="normal-dark-button modal-button"
            onClick={handleClose}
            size="middle"
          >
            Cancel
          </Button>
          <Button
            className="modal-button"
            type="primary"
            htmlType="submit"
            size="middle"
          >
            Filter
          </Button>
          <Button
            className="modal-button !border-primary !text-primary"
            onClick={handleResetFilter}
            size="middle"
          >
            Reset
          </Button>
        </Space>
      </Form>
    </CustomModal>
  );
}
